import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Field, FormActions, Input } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { ACCENT_COLOR_SWATCHES, ACCENT_COLORS } from '@/features/settings/branding';
import { getCompany, getCompanySettings, updateCompanySettings } from '@/features/settings/api';
import { uploadCompanyFile } from '@/features/uploads/api';
import { fileValidationError, IMAGE_EXTENSIONS } from '@/features/uploads/validate';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';
import { cn } from '@/lib/cn';
import type { AccentColor } from '@/api/types';

/**
 * `/settings/branding` — Instellingen › Huisstijl (§ WP-08): logo, een
 * accentkleur uit een vaste set, en een live voorbeeld. Written through
 * `PUT /companies/{id}/settings`, which is admin-only (`companies.settings.update`)
 * — a company admin sees the form read-only, same "the API is the boundary"
 * pattern as `CompanySettingsPage` under Beheer.
 *
 * Bestandsupload (PLAN-FASE3.md WP-07): the file picker uploads through
 * `POST /companies/{id}/uploads` as soon as a file is chosen, and the
 * returned URL is what gets saved as `logo_url` on submit — the manual
 * URL field below it still works too (a company that already hosts its
 * logo elsewhere need not re-upload it), it is simply no longer the only
 * way in.
 */
export function SettingsBrandingPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const { permissions } = useMe();
  const companyId = company ? company.id : null;

  const settingsState = useAsync(
    () => (companyId ? getCompanySettings(companyId) : Promise.reject(new Error('no company'))),
    [companyId],
  );
  const companyState = useAsync(
    () => (companyId ? getCompany(companyId) : Promise.reject(new Error('no company'))),
    [companyId],
  );

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [accentColor, setAccentColor] = useState<AccentColor | null | undefined>(undefined);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<Error | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  async function handleLogoFile(file: File) {
    if (!companyId) return;
    setUploadError(null);
    const validationError = fileValidationError(file, IMAGE_EXTENSIONS);
    if (validationError === 'tooLarge') {
      setUploadError(new Error(t('settings.branding.upload.tooLarge')));
      return;
    }
    if (validationError === 'invalidType') {
      setUploadError(new Error(t('settings.branding.upload.invalidType')));
      return;
    }
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      const result = await uploadCompanyFile(companyId, file);
      setLogoUrl(result.url);
    } catch (cause) {
      setUploadError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setUploading(false);
    }
  }

  const form = useSubmit(async () => {
    if (!companyId || !settingsState.data) return;
    await updateCompanySettings(companyId, {
      min_group_size: settingsState.data.min_group_size,
      logo_url: logoUrl ?? settingsState.data.logo_url ?? null,
      accent_color:
        accentColor !== undefined ? accentColor : (settingsState.data.accent_color ?? null),
    });
    settingsState.reload();
  });

  const canEdit = permissions.canUpdateCompanySettings;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={t('settings.branding.title')} lead={t('settings.branding.lead')} />

      {!companyId && <p className="text-accent text-sm">{t('settings.noCompany')}</p>}
      {companyId && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader title={t('settings.branding.title')} />
            <AsyncSection
              state={settingsState}
              label={t('settings.branding.title')}
              skeleton="card"
            >
              {(data) => {
                const currentLogo = logoUrl ?? data.logo_url ?? '';
                const currentAccent =
                  accentColor !== undefined ? accentColor : (data.accent_color ?? null);
                return (
                  <form
                    className="flex flex-col gap-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void form.submit();
                    }}
                  >
                    <Field
                      label={t('settings.branding.logo')}
                      hint={
                        uploading
                          ? t('settings.branding.upload.uploading')
                          : t('settings.branding.logoHint')
                      }
                      error={uploadError?.message}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        disabled={!canEdit || uploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void handleLogoFile(file);
                        }}
                        className="text-sm"
                      />
                    </Field>
                    <Field label={t('settings.branding.logoUrl')}>
                      <Input
                        value={currentLogo}
                        onChange={(e) => setLogoUrl(e.target.value)}
                        disabled={!canEdit}
                        placeholder="https://…"
                      />
                    </Field>
                    <Field label={t('settings.branding.accentColor')}>
                      <div
                        className="flex flex-wrap gap-2"
                        role="radiogroup"
                        aria-label={t('settings.branding.accentColor')}
                      >
                        {ACCENT_COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            role="radio"
                            aria-checked={currentAccent === c}
                            aria-label={t(`settings.branding.colors.${c}`)}
                            disabled={!canEdit}
                            onClick={() => setAccentColor(c)}
                            className={cn(
                              'size-8 rounded-full border-2 transition-transform motion-reduce:transition-none',
                              currentAccent === c ? 'border-ink scale-110' : 'border-transparent',
                              'disabled:opacity-50',
                            )}
                            style={{ backgroundColor: ACCENT_COLOR_SWATCHES[c] }}
                          />
                        ))}
                      </div>
                    </Field>
                    {!canEdit && (
                      <p className="text-accent text-[13px]">{t('settings.adminOnly')}</p>
                    )}
                    {canEdit && (
                      <>
                        <SubmitError error={form.error} />
                        <FormActions>
                          <Button type="submit" disabled={form.pending}>
                            {t('settings.save')}
                          </Button>
                        </FormActions>
                      </>
                    )}
                  </form>
                );
              }}
            </AsyncSection>
          </Card>

          <Card>
            <CardHeader title={t('settings.branding.preview')} />
            <AsyncSection
              state={companyState}
              label={t('settings.branding.preview')}
              skeleton="card"
            >
              {(company) => {
                const accent =
                  accentColor !== undefined
                    ? accentColor
                    : (settingsState.data?.accent_color ?? null);
                const swatch = accent ? ACCENT_COLOR_SWATCHES[accent] : '#22c55e';
                const logo = previewUrl ?? logoUrl ?? settingsState.data?.logo_url ?? null;
                return (
                  <div className="border-line flex flex-col items-center gap-3 rounded-2xl border p-6">
                    {logo ? (
                      <img src={logo} alt="" className="h-12 max-w-full object-contain" />
                    ) : (
                      <div
                        className="flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold text-white"
                        style={{ backgroundColor: swatch }}
                      >
                        {(company.name ?? '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <span
                      className="rounded-pill px-4 py-2 text-sm font-medium text-white"
                      style={{ backgroundColor: swatch }}
                    >
                      {company.name}
                    </span>
                  </div>
                );
              }}
            </AsyncSection>
          </Card>
        </div>
      )}
    </section>
  );
}
